import React from 'react';
import { useFela, ThemeProps } from '../..';
import { InputProps } from './InputProps';

const BORDER_WIDTH = {
  borderWidth: 2,
};

const defaultStyle = ({
  theme,
  disabled,
  invalid,
}: {
  theme: ThemeProps;
  disabled: InputProps['disabled'];
  invalid: InputProps['invalid'];
}) => ({
  outline: 'none',
  fontSize: theme.typography.fontSize.m.rem,
  fontFamily: theme.typography.fontFamily.primary.main,
  fontWeight: 400,
  color: theme.palette.color3.hex,
  backgroundColor: theme.palette.color5.hex,
  height: '44px',
  lineHeight: '44px',
  padding: 8,
  marginBottom: 10,
  borderStyle: 'solid',
  borderColor: theme.palette.color3.hex,
  borderRadius: theme.radius.s.rem,
  appearance: 'none',
  boxSizing: 'border-box',
  width: '100%',
  cursor: 'auto',
  '[type=text]': BORDER_WIDTH,
  '[type=date]': BORDER_WIDTH,
  '[type=email]': BORDER_WIDTH,
  '[type=password]': BORDER_WIDTH,
  '[type=number]': BORDER_WIDTH,
  ':focus': {
    borderColor: theme.palette.color2.hex,
    boxShadow: `0 0 5px ${theme.palette.color2.hex}`,
    BORDER_WIDTH,
    color: theme.palette.color3.hex,
  },
  '::-ms-reveal': { display: 'none' },
  '::-ms-clear': { display: 'none' },
  '::-webkit-input-placeholder': {
    color: theme.palette.color10.hex,
    lineHeight: '44px',
  },
  extend: [
    {
      condition: disabled,
      style: {
        backgroundColor: theme.palette.color9.hex,
        color: theme.palette.color10.hex,
        borderColor: theme.palette.color10.hex,
        cursor: 'not-allowed',
      },
    },
    {
      condition: invalid,
      style: {
        color: theme.palette.color8.hex,
        borderColor: theme.palette.color8.hex,
      },
    },
  ],
});

const Input = ({
  style,
  dmId = 'dm-input',
  id,
  disabled,
  required,
  invalid,
  reference,
  type = 'text',
  ...otherProps
}: InputProps): JSX.Element => {
  const { css } = useFela({ disabled, invalid });
  return (
    <input
      data-designsystem
      className={css(defaultStyle, style)}
      data-dmid={dmId}
      name={dmId}
      id={id || dmId} // todo: dmId should be removed, as soon as id prop is required (myt-7757)
      aria-required={required}
      aria-invalid={invalid}
      ref={reference}
      type={type}
      disabled={disabled}
      {...otherProps}
    />
  );
};

export default Input;
